import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import DocumentsTab from '../components/DocumentsTab';
import documentService from '../services/documentService';

const ProjectForm = () => {
  const { companyId, projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiProgress, setAiProgress] = useState('');
  const [aiPlan, setAiPlan] = useState(null);
  const [aiTasks, setAiTasks] = useState([]);
  const [loadingAITasks, setLoadingAITasks] = useState(false);
  const [aiActions, setAiActions] = useState([]);
  const [loadingAIActions, setLoadingAIActions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    project_type: 'marketing',
    budget: '',
    start_date: '',
    end_date: '',
    status: 'planning',
    priority: 'medium'
  });

  const isEditing = Boolean(projectId);

  const fetchCompany = useCallback(async () => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      setCompany(response.data);
    } catch (error) {
      console.error('Error fetching company:', error);
      setError('Failed to load company');
    }
  }, [companyId]);

  const fetchCampaigns = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingCampaigns(true);
      const response = await api.get(`/projects/${projectId}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [projectId]);

  const fetchAITasks = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingAITasks(true);
      const response = await api.get(`/projects/${projectId}/ai-tasks`);
      setAiTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching AI tasks:', error);
    } finally {
      setLoadingAITasks(false);
    }
  }, [projectId]);

  const fetchAIActions = useCallback(async () => {
    try {
      setLoadingAIActions(true);
      const response = await api.get('/ai-actions', {
        params: {
          target_entity: 'Project',
          target_form: 'ProjectForm'
        }
      });
      const actionsData = Array.isArray(response.data) ? response.data : (response.data.actions || []);
      setAiActions(actionsData.filter(action => action.is_active));
    } catch (error) {
      console.error('Error fetching AI actions:', error);
    } finally {
      setLoadingAIActions(false);
    }
  }, []);

  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/projects/${projectId}`);
      const projectData = response.data;
      setProject(projectData);
      setFormData({
        name: projectData.name,
        slug: projectData.slug,
        description: projectData.description || '',
        project_type: projectData.project_type,
        budget: projectData.budget || '',
        start_date: projectData.start_date ? projectData.start_date.split('T')[0] : '',
        end_date: projectData.end_date ? projectData.end_date.split('T')[0] : '',
        status: projectData.status,
        priority: projectData.priority
      });
    } catch (error) {
      console.error('Error fetching project:', error);
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [companyId, projectId]);

  useEffect(() => {
    fetchCompany();
    fetchAIActions();
    if (projectId) {
      fetchProject();
      fetchCampaigns();
      fetchAITasks();
    }
  }, [companyId, projectId, fetchCompany, fetchProject, fetchCampaigns, fetchAITasks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await api.put(`/companies/${companyId}/projects/${projectId}`, formData);
      } else {
        await api.post(`/companies/${companyId}/projects`, formData);
      }
      navigate(`/companies/${companyId}/edit?tab=projects`);
    } catch (error) {
      console.error('Error saving project:', error);
      setError(error.response?.data?.error || error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} project`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Auto-generate slug from name only when creating (not editing)
      ...(name === 'name' && !isEditing && { slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })
    }));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this project? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`/companies/${companyId}/projects/${projectId}`);
      navigate(`/companies/${companyId}/edit`);
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to delete project');
      setLoading(false);
    }
  };

  const handleAIRewrite = async () => {
    if (!isEditing) {
      setError('Please save the project first before using AI rewrite');
      return;
    }
    
    try {
      setAiLoading(true);
      setError(null);
      
      // First get preview
      const previewResponse = await api.post(`/projects/${projectId}/ai-rewrite`, { apply: false });
      setAiPreview(previewResponse.data.preview);
      
      // Confirm with user
      if (window.confirm('AI has generated enhanced content. Would you like to apply these changes?')) {
        // Apply changes
        const applyResponse = await api.post(`/projects/${projectId}/ai-rewrite`, { apply: true });
        
        // Update form with new data
        setFormData(prev => ({
          ...prev,
          name: applyResponse.data.project.name,
          description: applyResponse.data.project.description
        }));
        
        setError(null);
        alert('Project successfully enhanced with AI! ✨');
        fetchProject(); // Reload project data
      }
    } catch (error) {
      console.error('Error rewriting with AI:', error);
      setError(error.response?.data?.error || 'Failed to rewrite with AI');
    } finally {
      setAiLoading(false);
      setShowAIMenu(false);
    }
  };

  const handleAICreatePlan = async () => {
    if (!isEditing) {
      setError('Please save the project first before generating a plan');
      return;
    }
    
    try {
      setAiLoading(true);
      setError(null);
      
      // Generate plan preview
      const planResponse = await api.post(`/projects/${projectId}/ai-generate-plan`, { apply: false });
      setAiPlan(planResponse.data.plan);
      setActiveTab('ai-plan');
    } catch (error) {
      console.error('Error generating plan:', error);
      setError(error.response?.data?.error || 'Failed to generate plan');
    } finally {
      setAiLoading(false);
      setShowAIMenu(false);
    }
  };

  const handleExecuteAIAction = async (action) => {
    if (!isEditing) {
      setError('Please save the project first before executing AI actions');
      return;
    }

    try {
      setAiLoading(true);
      setError(null);
      setShowAIMenu(false);

      // Prepare input data from current form
      const inputData = {};
      if (action.input_fields && Array.isArray(action.input_fields)) {
        action.input_fields.forEach(field => {
          if (formData[field] !== undefined) {
            inputData[field] = formData[field];
          }
        });
      }

      // Execute the AI action
      setAiProgress('Starting AI action...');
      const response = await api.post(`/ai-actions/${action.id}/execute`, {
        entity_type: 'Project',
        entity_id: projectId,
        input_data: inputData
      }, {
        timeout: 180000 // 3 minute timeout
      });

      const execution = response.data;
      
      // Poll for completion if status is running
      if (execution.status === 'running') {
        setAiProgress('AI is processing your request...');
        await pollExecutionStatus(execution.execution_id, action);
      } else if (execution.status === 'completed') {
        // If action requires confirmation, show preview
        if (action.require_confirmation && execution.output_data) {
          setAiProgress('');
          setAiPreview({
            execution_id: execution.execution_id,
            action_name: action.name,
            output_data: execution.output_data,
            action: action
          });
        } else if (execution.applied_changes) {
          // Auto-applied, update form with new data
          const updatedData = { ...formData };
          Object.keys(execution.applied_changes).forEach(key => {
            if (updatedData.hasOwnProperty(key)) {
              updatedData[key] = execution.applied_changes[key];
            }
          });
          setFormData(updatedData);
          setAiProgress('');
          alert(`✨ ${action.name} completed successfully!`);
        }
      }
    } catch (error) {
      console.error('Error executing AI action:', error);
      setError(error.response?.data?.error || 'Failed to execute AI action');
    } finally {
      setAiLoading(false);
    }
  };

  const pollExecutionStatus = async (executionId, action, maxAttempts = 60) => {
    let attempts = 0;
    const pollInterval = 2000; // Poll every 2 seconds

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
      
      try {
        const statusResponse = await api.get(`/ai-actions/executions/${executionId}`);
        const execution = statusResponse.data;
        
        const elapsed = attempts * 2;
        setAiProgress(`AI is processing... (${elapsed}s)`);
        
        if (execution.status === 'completed') {
          setAiProgress('');
          if (action.require_confirmation && execution.output_data) {
            setAiPreview({
              execution_id: execution.id,
              action_name: action.name,
              output_data: execution.output_data,
              action: action
            });
          }
          return;
        } else if (execution.status === 'failed') {
          setAiProgress('');
          throw new Error(execution.error_message || 'AI action failed');
        }
      } catch (error) {
        if (error.response?.status === 404) {
          const elapsed = attempts * 2;
          setAiProgress(`AI is processing... (${elapsed}s)`);
          continue;
        }
        throw error;
      }
    }
    
    throw new Error('AI action timed out after 2 minutes');
  };

  const handleApplyAIPlan = async () => {
    try {
      setAiLoading(true);
      
      // Apply the plan and create tasks
      const response = await api.post(`/projects/${projectId}/ai-generate-plan`, { apply: true });
      
      alert(`Success! Created ${response.data.total_tasks} AI agent tasks for this project. 🎯`);
      setAiPlan(null);
      await fetchAITasks(); // Refresh AI tasks list
    } catch (error) {
      console.error('Error applying plan:', error);
      setError(error.response?.data?.error || 'Failed to apply plan');
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyAIActionChanges = async () => {
    if (!aiPreview) return;

    try {
      setAiLoading(true);

      // Update form data with the AI-generated changes
      const updatedData = { ...formData };
      Object.keys(aiPreview.output_data).forEach(key => {
        if (updatedData.hasOwnProperty(key)) {
          updatedData[key] = aiPreview.output_data[key];
        }
      });
      setFormData(updatedData);

      // Optionally confirm the execution on backend
      if (aiPreview.execution_id) {
        await api.post(`/ai-actions/executions/${aiPreview.execution_id}/confirm`);
      }

      // Save AI-generated content as a document if it's markdown/text content
      try {
        const outputText = Object.values(aiPreview.output_data).join('\n\n');
        if (outputText && outputText.length > 100) {
          await documentService.createMarkdownDocument({
            title: `${aiPreview.action_name} - ${formData.name || 'Project'}`,
            content: outputText,
            description: `AI-generated content from ${aiPreview.action_name}`,
            project_id: projectId,
            category: 'ai-generated',
            tags: ['ai-generated', 'strategy', aiPreview.action_name.toLowerCase().replace(/\s+/g, '-')],
            source: 'AI_ACTION',
            ai_action_execution_id: aiPreview.execution_id
          });
        }
      } catch (docError) {
        console.warn('Failed to save document:', docError);
        // Don't fail the whole operation if document save fails
      }

      alert(`✨ ${aiPreview.action_name} changes applied successfully!`);
      setAiPreview(null);
      
      // Refresh documents tab if it's active
      if (activeTab === 'documents') {
        // The DocumentsTab will auto-refresh on mount
      }
    } catch (error) {
      console.error('Error applying AI changes:', error);
      setError(error.response?.data?.error || 'Failed to apply changes');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCancelAIActionChanges = () => {
    setAiPreview(null);
  };

  if (loading && isEditing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/companies')}
          className="breadcrumb-link"
        >
          Companies
        </button>
        <span className="breadcrumb-separator">/</span>
        <button
          onClick={() => navigate(`/companies/${companyId}/edit`)}
          className="breadcrumb-link"
        >
          {company?.name}
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditing ? 'Edit Project' : 'New Project'}
        </span>
      </nav>

      {/* Project Form Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditing ? 'Edit Project' : 'Create New Project'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {isEditing ? 'Update project information' : `Add a new project to ${company?.name || 'company'}`}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          marginTop: '5px',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--gray-200)'
        }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="btn btn-danger"
              >
                🗑️ Delete
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            {isEditing && (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setShowAIMenu(!showAIMenu)}
                  disabled={loading || aiLoading}
                  className="btn btn-primary"
                  style={{ backgroundColor: '#7C3AED', borderColor: '#7C3AED' }}
                >
                  🤖 AI
                </button>
                
                {showAIMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    backgroundColor: 'white',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: '250px'
                  }}>
                    {/* Dynamic AI Actions */}
                    {aiActions.length === 0 ? (
                      <div style={{
                        padding: '1rem',
                        textAlign: 'center',
                        color: 'var(--gray-500)',
                        fontSize: '0.875rem'
                      }}>
                        No AI actions configured for this form
                      </div>
                    ) : (
                      aiActions.map((action) => (
                        <button
                          key={action.id}
                          onClick={() => handleExecuteAIAction(action)}
                          disabled={aiLoading}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            textAlign: 'left',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: '0.5rem'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--gray-50)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <span>{action.icon || '⚡'}</span>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: '600', color: 'var(--gray-900)', textAlign: 'left' }}>
                              {action.name}
                            </div>
                            {action.description && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', textAlign: 'left' }}>
                                {action.description}
                              </div>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            
            <button
              type="button"
              onClick={() => navigate(`/companies/${companyId}/edit?tab=projects`)}
              disabled={loading}
              className="btn btn-outline"
            >
              ✕ Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              form="project-form"
              title="Update Project"
            >
              {loading ? 'Saving...' : (isEditing ? '💾 Update' : 'Create Project')}
            </button>
          </div>
        </div>

        <form id="project-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Project Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter project name"
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Slug *
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                required
                placeholder="project-slug"
                style={{ fontFamily: 'monospace' }}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: '600', 
              color: 'var(--gray-700)',
              fontSize: '0.875rem'
            }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="6"
              placeholder="Brief description of the project"
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Project Type
              </label>
              <select
                name="project_type"
                value={formData.project_type}
                onChange={handleInputChange}
              >
                <option value="marketing">Marketing</option>
                <option value="product">Product</option>
                <option value="sales">Sales</option>
                <option value="research">Research</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Budget
              </label>
              <input
                type="number"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                End Date
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Tabs */}
      {isEditing && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            gap: '2rem', 
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '1.5rem'
          }}>
            <button
              onClick={() => setActiveTab('campaigns')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'campaigns' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'campaigns' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              📋 Campaigns ({campaigns.length})
            </button>
            <button
              onClick={() => setActiveTab('ai-plan')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'ai-plan' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'ai-plan' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              🤖 AI Plan ({aiTasks.length})
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'documents' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'documents' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              📄 Documents
            </button>
          </div>

          {/* Campaigns Tab Content */}
          {activeTab === 'campaigns' && (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>Campaigns</h3>
                <button
                  onClick={() => navigate(`/companies/${companyId}/projects/${projectId}/campaigns/new`)}
                  className="btn btn-primary"
                  title="Add Campaign"
                >
                  ➕ Add
                </button>
              </div>

              {loadingCampaigns ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div className="spinner"></div>
                </div>
              ) : campaigns.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  color: 'var(--gray-500)'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                  <p style={{ margin: '0 0 1rem 0' }}>No campaigns yet</p>
                  <button
                    onClick={() => navigate(`/companies/${companyId}/projects/${projectId}/campaigns/new`)}
                    className="btn btn-primary"
                  >
                    Create First Campaign
                  </button>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: 'var(--gray-700)',
                        fontSize: '0.875rem'
                      }}>
                        Campaign Name
                      </th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: 'var(--gray-700)',
                        fontSize: '0.875rem'
                      }}>
                        Type
                      </th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: 'var(--gray-700)',
                        fontSize: '0.875rem'
                      }}>
                        Channel
                      </th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontWeight: '600', 
                        color: 'var(--gray-700)',
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </th>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        fontWeight: '600', 
                        color: 'var(--gray-700)',
                        fontSize: '0.875rem'
                      }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ fontWeight: '500', color: 'var(--gray-900)' }}>
                            {campaign.name}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: 'var(--gray-700)', textTransform: 'capitalize' }}>
                          {campaign.campaign_type}
                        </td>
                        <td style={{ padding: '1rem', color: 'var(--gray-700)', textTransform: 'capitalize' }}>
                          {campaign.channel}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: campaign.status === 'active' ? 'var(--success-100)' : 
                                           campaign.status === 'completed' ? 'var(--gray-200)' :
                                           campaign.status === 'paused' ? 'var(--warning-100)' : 'var(--gray-100)',
                            color: campaign.status === 'active' ? 'var(--success-700)' : 
                                   campaign.status === 'completed' ? 'var(--gray-700)' :
                                   campaign.status === 'paused' ? 'var(--warning-700)' : 'var(--gray-600)',
                            textTransform: 'capitalize'
                          }}>
                            {campaign.status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'right' }}>
                          <button
                            onClick={() => navigate(`/companies/${companyId}/projects/${projectId}/campaigns/${campaign.id}/edit`)}
                            style={{
                              backgroundColor: 'var(--gray-100)',
                              color: 'var(--gray-700)',
                              border: '1px solid var(--gray-300)',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '8px',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                          >
                            ✏️ Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* AI Plan Tab Content */}
          {activeTab === 'ai-plan' && (
            <>
              {aiPlan ? (
                <div>
                  <div style={{
                    backgroundColor: 'var(--primary-50)',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ margin: 0, color: 'var(--gray-700)' }}>
                      {aiPlan.plan_summary}
                    </p>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                      <strong>Estimated Duration:</strong> {aiPlan.total_estimated_days} days
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    {aiPlan.phases && aiPlan.phases.map((phase, phaseIndex) => (
                <div key={phaseIndex} style={{
                  marginBottom: '1.5rem',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '1px solid var(--gray-200)'
                  }}>
                    <h4 style={{ margin: 0, color: 'var(--gray-900)' }}>
                      {phase.phase_name}
                    </h4>
                    <span style={{
                      backgroundColor: 'var(--gray-100)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--gray-700)'
                    }}>
                      {phase.duration_days} days
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {phase.tasks && phase.tasks.map((task, taskIndex) => (
                      <div key={taskIndex} style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--gray-50)',
                        borderRadius: '6px',
                        borderLeft: `3px solid ${
                          task.priority === 'high' ? '#EF4444' :
                          task.priority === 'medium' ? '#F59E0B' :
                          '#10B981'
                        }`
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              color: 'var(--gray-900)',
                              marginBottom: '0.25rem'
                            }}>
                              {task.name}
                            </div>
                            <div style={{
                              fontSize: '0.875rem',
                              color: 'var(--gray-600)',
                              marginBottom: '0.5rem'
                            }}>
                              {task.description}
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              flexWrap: 'wrap',
                              fontSize: '0.75rem'
                            }}>
                              <span style={{
                                backgroundColor: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--gray-300)'
                              }}>
                                👤 {task.agent_type?.replace('_', ' ') || 'General'}
                              </span>
                              <span style={{
                                backgroundColor: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--gray-300)'
                              }}>
                                ⏱️ {task.duration_days} days
                              </span>
                              <span style={{
                                backgroundColor: task.can_automate ? 'var(--success-100)' : 'var(--warning-100)',
                                color: task.can_automate ? 'var(--success-700)' : 'var(--warning-700)',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px'
                              }}>
                                {task.can_automate ? '🤖 Automated' : '👨 Manual'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end',
                    paddingTop: '1rem',
                    marginTop: '1.5rem',
                    borderTop: '1px solid var(--gray-200)'
                  }}>
                    <button
                      onClick={() => setAiPlan(null)}
                      className="btn btn-outline"
                      disabled={aiLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApplyAIPlan}
                      className="btn btn-primary"
                      disabled={aiLoading}
                    >
                      {aiLoading ? 'Creating Tasks...' : '✓ Apply Plan & Create Tasks'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>AI Agent Tasks</h3>
                    <button
                      onClick={handleAICreatePlan}
                      className="btn btn-primary"
                      disabled={aiLoading}
                      title="Generate new AI plan"
                    >
                      {aiLoading ? 'Generating...' : '✨ Generate Plan'}
                    </button>
                  </div>

                  {loadingAITasks ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                      <div className="spinner"></div>
                    </div>
                  ) : aiTasks.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      backgroundColor: 'var(--gray-50)',
                      borderRadius: '8px',
                      border: '2px dashed var(--gray-300)'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤖</div>
                      <h3 style={{ color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                        No AI Tasks Yet
                      </h3>
                      <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem' }}>
                        Generate an AI-powered project plan to create automated tasks
                      </p>
                      <button
                        onClick={handleAICreatePlan}
                        className="btn btn-primary"
                        disabled={aiLoading}
                      >
                        {aiLoading ? 'Generating...' : '✨ Generate AI Plan'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {aiTasks.map((task) => (
                        <div
                          key={task.id}
                          style={{
                            padding: '1rem',
                            backgroundColor: 'var(--gray-50)',
                            borderRadius: '8px',
                            border: '1px solid var(--gray-200)',
                            borderLeft: `4px solid ${
                              task.status === 'completed' ? '#10B981' :
                              task.status === 'in_progress' ? '#3B82F6' :
                              task.status === 'failed' ? '#EF4444' :
                              '#6B7280'
                            }`
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                            marginBottom: '0.5rem'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '600',
                                color: 'var(--gray-900)',
                                marginBottom: '0.25rem'
                              }}>
                                {task.task_name}
                              </div>
                              <div style={{
                                fontSize: '0.875rem',
                                color: 'var(--gray-600)',
                                marginBottom: '0.5rem'
                              }}>
                                {task.description}
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  backgroundColor: 'var(--primary-100)',
                                  color: 'var(--primary-700)'
                                }}>
                                  {task.agent_type || 'general'}
                                </span>
                                <span style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  backgroundColor: 
                                    task.status === 'completed' ? 'var(--success-100)' :
                                    task.status === 'in_progress' ? 'var(--info-100)' :
                                    task.status === 'failed' ? 'var(--danger-100)' :
                                    'var(--gray-200)',
                                  color:
                                    task.status === 'completed' ? 'var(--success-700)' :
                                    task.status === 'in_progress' ? 'var(--info-700)' :
                                    task.status === 'failed' ? 'var(--danger-700)' :
                                    'var(--gray-700)',
                                  textTransform: 'capitalize'
                                }}>
                                  {task.status?.replace('_', ' ')}
                                </span>
                                {task.priority && (
                                  <span style={{
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    backgroundColor:
                                      task.priority === 'high' ? '#FEE2E2' :
                                      task.priority === 'medium' ? '#FEF3C7' :
                                      '#D1FAE5',
                                    color:
                                      task.priority === 'high' ? '#991B1B' :
                                      task.priority === 'medium' ? '#92400E' :
                                      '#065F46'
                                  }}>
                                    {task.priority}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Documents Tab Content */}
          {activeTab === 'documents' && projectId && (
            <DocumentsTab projectId={projectId} />
          )}
        </div>
      )}

      {/* AI Action Confirmation Modal */}
      {aiPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.5rem',
              color: 'var(--gray-900)'
            }}>
              ⚡ {aiPreview.action_name}
            </h3>
            <p style={{
              margin: '0 0 1.5rem 0',
              color: 'var(--gray-600)'
            }}>
              Review the AI-generated changes below. Click "Apply Changes" to update the form.
            </p>

            <div style={{
              backgroundColor: 'var(--gray-50)',
              border: '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              {Object.keys(aiPreview.output_data).map((key) => (
                <div key={key} style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    color: 'var(--gray-700)',
                    marginBottom: '0.5rem',
                    textTransform: 'capitalize'
                  }}>
                    {key.replace(/_/g, ' ')}
                  </label>
                  <div style={{
                    backgroundColor: 'white',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid var(--gray-300)',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}>
                    {aiPreview.output_data[key]}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCancelAIActionChanges}
                className="btn btn-outline"
                disabled={aiLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyAIActionChanges}
                className="btn btn-primary"
                disabled={aiLoading}
              >
                {aiLoading ? 'Applying...' : 'Apply Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectForm;
